/**
 * Leaderboards Page
 *
 * Multiple leaderboard views:
 * - Top Creators (by challenges created)
 * - Most Submissions (by submission count)
 * - Trending Challenges (by recent activity)
 * - Top Voters (by votes cast)
 *
 * Features:
 * - Tab switching between leaderboards
 * - Pagination
 * - Time period filtering (All time, This month, This week)
 * - User/challenge preview
 * - Rankings with badges
 */

import { Trophy, Crown, Flame, TrendingUp, Medal } from 'lucide-react';
import { useState, useMemo } from 'react';

import { cn } from '@/lib/cn';

type LeaderboardType = 'creators' | 'submissions' | 'challenges' | 'voters';
type TimePeriod = 'all-time' | 'month' | 'week';

interface CreatorRank {
  rank: number;
  userId: string;
  displayName: string;
  avatar: string;
  challengesCreated: number;
  totalSubmissions: number;
  totalJems: number;
}

interface SubmissionRank {
  rank: number;
  userId: string;
  displayName: string;
  avatar: string;
  submissionsCount: number;
  jemsCastedCount: number;
  categoriesParticipated: number;
}

interface ChallengeRank {
  rank: number;
  challengeId: string;
  title: string;
  creatorName: string;
  category: string;
  submissionCount: number;
  totalJems: number;
  trending: number; // Recent activity score
}

interface VoterRank {
  rank: number;
  userId: string;
  displayName: string;
  avatar: string;
  votesCount: number;
  challengesParticipated: number;
}

// Mock data
const mockTopCreators: CreatorRank[] = [
  {
    rank: 1,
    userId: 'user1',
    displayName: 'Alex Chen',
    avatar: 'https://picsum.photos/48/48?random=1',
    challengesCreated: 12,
    totalSubmissions: 248,
    totalJems: 3420,
  },
  {
    rank: 2,
    userId: 'user2',
    displayName: 'Jordan Smith',
    avatar: 'https://picsum.photos/48/48?random=2',
    challengesCreated: 10,
    totalSubmissions: 186,
    totalJems: 2840,
  },
  {
    rank: 3,
    userId: 'user3',
    displayName: 'Sam Wilson',
    avatar: 'https://picsum.photos/48/48?random=3',
    challengesCreated: 9,
    totalSubmissions: 172,
    totalJems: 2560,
  },
  {
    rank: 4,
    userId: 'user4',
    displayName: 'Taylor Brown',
    avatar: 'https://picsum.photos/48/48?random=4',
    challengesCreated: 8,
    totalSubmissions: 154,
    totalJems: 2180,
  },
  {
    rank: 5,
    userId: 'user5',
    displayName: 'Morgan Lee',
    avatar: 'https://picsum.photos/48/48?random=5',
    challengesCreated: 7,
    totalSubmissions: 142,
    totalJems: 1920,
  },
];

const mockTopSubmitters: SubmissionRank[] = [
  {
    rank: 1,
    userId: 'user6',
    displayName: 'Casey Johnson',
    avatar: 'https://picsum.photos/48/48?random=6',
    submissionsCount: 64,
    jemsCastedCount: 127,
    categoriesParticipated: 5,
  },
  {
    rank: 2,
    userId: 'user7',
    displayName: 'Riley Davis',
    avatar: 'https://picsum.photos/48/48?random=7',
    submissionsCount: 52,
    jemsCastedCount: 103,
    categoriesParticipated: 4,
  },
  {
    rank: 3,
    userId: 'user8',
    displayName: 'Jamie Martin',
    avatar: 'https://picsum.photos/48/48?random=8',
    submissionsCount: 48,
    jemsCastedCount: 96,
    categoriesParticipated: 5,
  },
  {
    rank: 4,
    userId: 'user9',
    displayName: 'Quinn Rodriguez',
    avatar: 'https://picsum.photos/48/48?random=9',
    submissionsCount: 41,
    jemsCastedCount: 82,
    categoriesParticipated: 3,
  },
  {
    rank: 5,
    userId: 'user10',
    displayName: 'Blake Taylor',
    avatar: 'https://picsum.photos/48/48?random=10',
    submissionsCount: 38,
    jemsCastedCount: 76,
    categoriesParticipated: 4,
  },
];

const mockTrendingChallenges: ChallengeRank[] = [
  {
    rank: 1,
    challengeId: 'ch1',
    title: 'Best Coffee Shop Photo',
    creatorName: 'Alex Chen',
    category: 'Photography',
    submissionCount: 87,
    totalJems: 1240,
    trending: 9.8,
  },
  {
    rank: 2,
    challengeId: 'ch2',
    title: 'Homemade Dessert Recipe',
    creatorName: 'Jordan Smith',
    category: 'Cooking',
    submissionCount: 56,
    totalJems: 892,
    trending: 8.7,
  },
  {
    rank: 3,
    challengeId: 'ch3',
    title: 'Acoustic Song Cover',
    creatorName: 'Sam Wilson',
    category: 'Singing',
    submissionCount: 42,
    totalJems: 678,
    trending: 7.9,
  },
  {
    rank: 4,
    challengeId: 'ch4',
    title: 'Classic Car Show',
    creatorName: 'Taylor Brown',
    category: 'Cars',
    submissionCount: 38,
    totalJems: 567,
    trending: 6.8,
  },
  {
    rank: 5,
    challengeId: 'ch5',
    title: 'Musical Production',
    creatorName: 'Morgan Lee',
    category: 'Music',
    submissionCount: 31,
    totalJems: 456,
    trending: 5.9,
  },
];

const mockTopVoters: VoterRank[] = [
  {
    rank: 1,
    userId: 'user11',
    displayName: 'Alex Thompson',
    avatar: 'https://picsum.photos/48/48?random=11',
    votesCount: 287,
    challengesParticipated: 45,
  },
  {
    rank: 2,
    userId: 'user12',
    displayName: 'Jordan Patterson',
    avatar: 'https://picsum.photos/48/48?random=12',
    votesCount: 264,
    challengesParticipated: 42,
  },
  {
    rank: 3,
    userId: 'user13',
    displayName: 'Sam Anderson',
    avatar: 'https://picsum.photos/48/48?random=13',
    votesCount: 248,
    challengesParticipated: 40,
  },
  {
    rank: 4,
    userId: 'user14',
    displayName: 'Casey Williams',
    avatar: 'https://picsum.photos/48/48?random=14',
    votesCount: 223,
    challengesParticipated: 36,
  },
  {
    rank: 5,
    userId: 'user15',
    displayName: 'Morgan Jackson',
    avatar: 'https://picsum.photos/48/48?random=15',
    votesCount: 198,
    challengesParticipated: 32,
  },
];

/**
 * Community leaderboards showing top creators, submitters, and challenges.
 */
export function LeaderboardPage() {
  const [activeBoard, setActiveBoard] = useState<LeaderboardType>('creators');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all-time');

  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="font-semibold text-slate-600 w-5 text-center">{rank}</span>;
    }
  };

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="h-8 w-8 text-brand-600" />
          <h1 className="text-3xl font-bold text-slate-900">Leaderboards</h1>
        </div>
        <p className="text-slate-600">See who's making Jemmi awesome</p>
      </div>

      {/* Board Selector */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveBoard('creators')}
          className={cn(
            'px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all',
            activeBoard === 'creators'
              ? 'bg-brand-600 text-white'
              : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300',
          )}
        >
          Top Creators
        </button>
        <button
          onClick={() => setActiveBoard('submissions')}
          className={cn(
            'px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all',
            activeBoard === 'submissions'
              ? 'bg-brand-600 text-white'
              : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300',
          )}
        >
          Most Submissions
        </button>
        <button
          onClick={() => setActiveBoard('challenges')}
          className={cn(
            'px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all',
            activeBoard === 'challenges'
              ? 'bg-brand-600 text-white'
              : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300',
          )}
        >
          <Flame className="h-4 w-4 inline mr-1" />
          Trending
        </button>
        <button
          onClick={() => setActiveBoard('voters')}
          className={cn(
            'px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all',
            activeBoard === 'voters'
              ? 'bg-brand-600 text-white'
              : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300',
          )}
        >
          Top Voters
        </button>
      </div>

      {/* Time Period Filter */}
      <div className="mb-6 flex gap-2">
        {(['all-time', 'month', 'week'] as const).map((period) => (
          <button
            key={period}
            onClick={() => setTimePeriod(period)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              timePeriod === period
                ? 'bg-brand-100 text-brand-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
            )}
          >
            {period === 'all-time' ? 'All Time' : period === 'month' ? 'This Month' : 'This Week'}
          </button>
        ))}
      </div>

      {/* Leaderboard Content */}
      <div className="space-y-2">
        {activeBoard === 'creators' && (
          <>
            {mockTopCreators.map((creator) => (
              <div
                key={creator.userId}
                className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  {/* Rank Badge */}
                  <div className="flex items-center justify-center w-8">
                    {getRankBadge(creator.rank)}
                  </div>

                  {/* Avatar & Name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <img
                      src={creator.avatar}
                      alt={creator.displayName}
                      className="h-10 w-10 rounded-full flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{creator.displayName}</p>
                      <p className="text-xs text-slate-500">
                        {creator.challengesCreated} challenges • {creator.totalSubmissions} submissions
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <TrendingUp className="h-4 w-4 text-brand-600" />
                      <span className="font-bold text-brand-600">{creator.totalJems}</span>
                    </div>
                    <p className="text-xs text-slate-500">jems</p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {activeBoard === 'submissions' && (
          <>
            {mockTopSubmitters.map((submitter) => (
              <div
                key={submitter.userId}
                className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  {/* Rank Badge */}
                  <div className="flex items-center justify-center w-8">
                    {getRankBadge(submitter.rank)}
                  </div>

                  {/* Avatar & Name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <img
                      src={submitter.avatar}
                      alt={submitter.displayName}
                      className="h-10 w-10 rounded-full flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{submitter.displayName}</p>
                      <p className="text-xs text-slate-500">
                        {submitter.categoriesParticipated} categories
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-slate-900">{submitter.submissionsCount}</div>
                    <p className="text-xs text-slate-500">submissions</p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {activeBoard === 'challenges' && (
          <>
            {mockTrendingChallenges.map((challenge) => (
              <div
                key={challenge.challengeId}
                className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  {/* Rank Badge */}
                  <div className="flex items-center justify-center w-8 pt-1">
                    {getRankBadge(challenge.rank)}
                  </div>

                  {/* Challenge Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{challenge.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      by {challenge.creatorName} • {challenge.category}
                    </p>
                  </div>

                  {/* Trending Indicator */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end mb-1">
                        <Flame className="h-4 w-4 text-orange-500" />
                        <span className="font-bold text-orange-500">{challenge.trending.toFixed(1)}</span>
                      </div>
                      <p className="text-xs text-slate-500">{challenge.submissionCount} subs</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {activeBoard === 'voters' && (
          <>
            {mockTopVoters.map((voter) => (
              <div
                key={voter.userId}
                className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  {/* Rank Badge */}
                  <div className="flex items-center justify-center w-8">
                    {getRankBadge(voter.rank)}
                  </div>

                  {/* Avatar & Name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <img
                      src={voter.avatar}
                      alt={voter.displayName}
                      className="h-10 w-10 rounded-full flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{voter.displayName}</p>
                      <p className="text-xs text-slate-500">
                        {voter.challengesParticipated} challenges voted
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-slate-900">{voter.votesCount}</div>
                    <p className="text-xs text-slate-500">votes cast</p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Info Footer */}
      <div className="mt-8 rounded-lg bg-blue-50 border border-blue-200 p-4">
        <p className="text-sm text-blue-900">
          <span className="font-semibold">💡 How rankings work:</span>
          {' '}
          Top Creators are ranked by total jems received. Most Submissions by submission count. Trending shows
          recent activity. Top Voters by votes cast.
        </p>
      </div>

      {/* Spacer for mobile bottom nav */}
      <div className="h-8" />
    </>
  );
}
